import { FRONTEND_VERSION_LENGTH, FRONTEND_VERSION_PARAM } from './constants';

export type FrontendVersionStatus =
  | { status: 'valid'; version: string }
  | { status: 'too-short'; invalidVersion: string }
  | { status: 'unknown'; invalidVersion: string }
  | { status: 'none' };

/**
 * Extract the SHA from the data-manifest-name attribute on the html element
 * @returns The SHA from the manifest name, or null if not found
 */
function getManifestSha(): string | null {
  const manifestName = document.documentElement.getAttribute('data-manifest-name');
  if (!manifestName) {
    return null;
  }

  // Extract SHA from format like "manifest.6b64b4200.json"
  const match = manifestName.match(/^manifest\.([a-f0-9]+)\.json$/);
  return match ? match[1] : null;
}

/**
 * Normalize the frontend version parameter in the URL if it's longer than expected
 * This ensures the backend receives the trimmed 9-character version
 */
function normalizeVersionInUrl(frontendVersion: string): void {
  if (frontendVersion.length > FRONTEND_VERSION_LENGTH) {
    const url = new URL(window.location.href);
    url.searchParams.set(FRONTEND_VERSION_PARAM, frontendVersion.slice(0, FRONTEND_VERSION_LENGTH));
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Get the frontend version override status from URL parameters
 * @returns An object describing the version override status
 */
export function getFrontendVersionOverrideStatus(): FrontendVersionStatus {
  const urlParams = new URLSearchParams(window.location.search);
  const frontendVersion = urlParams.get(FRONTEND_VERSION_PARAM);

  if (!frontendVersion) {
    return { status: 'none' };
  }

  if (frontendVersion.length < FRONTEND_VERSION_LENGTH) {
    return { status: 'too-short', invalidVersion: frontendVersion };
  }

  // Accept versions of the expected length or longer (trim if longer)
  const trimmedVersion = frontendVersion.slice(0, FRONTEND_VERSION_LENGTH);

  // If the version is longer than expected, normalize it in the URL
  // This ensures the backend receives the trimmed version
  normalizeVersionInUrl(frontendVersion);

  // Validate that the SHA matches the manifest name, which indicates
  // the frontend successfully loaded the requested version
  const manifestSha = getManifestSha();
  if (manifestSha && !manifestSha.startsWith(trimmedVersion)) {
    return { status: 'unknown', invalidVersion: trimmedVersion };
  }

  return { status: 'valid', version: trimmedVersion };
}

/**
 * Get the frontend version override from URL parameters
 * @returns The frontend version from URL parameters or null if not set/invalid
 */
export function getFrontendVersionOverride(): string | null {
  const status = getFrontendVersionOverrideStatus();
  return status.status === 'valid' ? status.version : null;
}

/**
 * Clear the frontend version override by removing it from the current URL
 */
export function clearFrontendVersionOverride(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(FRONTEND_VERSION_PARAM);

  // Update the URL without causing a page reload
  window.history.replaceState({}, '', url.toString());
}

/**
 * Check if a frontend version override is currently active
 * @returns True if a valid version parameter exists in the URL
 */
export function hasFrontendVersionOverride(): boolean {
  return getFrontendVersionOverride() !== null;
}
